import React from 'react';
import { RadioButton } from 'primereact/radiobutton';
import { SelectButton } from 'primereact/selectbutton';
import {
  CaddiePrimaryFocus,
  CaddieRecommendationDepth,
  CaddieTalkativeness,
  CaddieVoicePreference,
  CaddieVoiceStyle,
} from '../types';

interface CaddieAdjustmentsProps {
  voicePreference: CaddieVoicePreference;
  onVoicePreferenceChange: (value: CaddieVoicePreference) => void;
  talkativeness: CaddieTalkativeness;
  onTalkativenessChange: (value: CaddieTalkativeness) => void;
  voiceStyle: CaddieVoiceStyle;
  onVoiceStyleChange: (value: CaddieVoiceStyle) => void;
  recommendationDepth: CaddieRecommendationDepth;
  onRecommendationDepthChange: (value: CaddieRecommendationDepth) => void;
  primaryFocus: CaddiePrimaryFocus;
  onPrimaryFocusChange: (value: CaddiePrimaryFocus) => void;
}

interface RadioOption<T extends string> {
  label: string;
  value: T;
  detail: string;
}

const CADDIE_VOICE_OPTIONS: { label: string; value: CaddieVoicePreference }[] = [
  { label: 'Female', value: 'female' },
  { label: 'Male', value: 'male' },
];

const VOICE_DELIVERY_OPTIONS: RadioOption<CaddieVoiceStyle>[] = [
  { label: 'Calm', value: 'calm', detail: 'Measured and steady over the ball.' },
  { label: 'Balanced', value: 'balanced', detail: 'Confident without over-talking.' },
  { label: 'Fired up', value: 'amped', detail: 'More energy when the shot matters.' },
];

const DETAIL_OPTIONS: RadioOption<CaddieRecommendationDepth>[] = [
  { label: 'Quick read', value: 'quick', detail: 'Club, target, one key cue.' },
  { label: 'Standard', value: 'standard', detail: 'Enough context to commit.' },
  { label: 'Deep dive', value: 'deep', detail: 'More shot planning and tradeoffs.' },
];

const FOCUS_OPTIONS: RadioOption<CaddiePrimaryFocus>[] = [
  { label: 'Course strategy', value: 'strategy', detail: 'Target, miss, and smarter angles.' },
  { label: 'Lie reading', value: 'lie', detail: 'Surface, stance, strike, and contact.' },
  { label: 'Swing cues', value: 'swing', detail: 'Tempo, shape, and setup reminders.' },
  { label: 'All the above', value: 'all', detail: 'Blend strategy, lie, and swing together.' },
];

const TALKATIVENESS_OPTIONS: RadioOption<CaddieTalkativeness>[] = [
  { label: 'Succinct', value: 'succinct', detail: 'Short, sharp, and straight to the point.' },
  { label: 'Wordy', value: 'wordy', detail: 'Full explanation with extra context.' },
];

function renderRadioGroup<T extends string>(
  name: string,
  legend: string,
  value: T,
  options: RadioOption<T>[],
  onChange: (value: T) => void,
) {
  return (
    <fieldset className="adjustment-radio-group">
      <legend>{legend}</legend>
      <div className="adjustment-options">
        {options.map((option) => {
          const inputId = `caddie-${name}-${option.value}`;
          return (
            <label key={option.value} className="adjustment-option" htmlFor={inputId}>
              <RadioButton
                inputId={inputId}
                name={name}
                value={option.value}
                checked={value === option.value}
                onChange={() => onChange(option.value)}
              />
              <span>
                <strong>{option.label}</strong>
                <small>{option.detail}</small>
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

const CaddieAdjustments: React.FC<CaddieAdjustmentsProps> = ({
  voicePreference,
  onVoicePreferenceChange,
  talkativeness,
  onTalkativenessChange,
  voiceStyle,
  onVoiceStyleChange,
  recommendationDepth,
  onRecommendationDepthChange,
  primaryFocus,
  onPrimaryFocusChange,
}) => {
  return (
    <section className="caddie-adjustments" aria-labelledby="caddie-adjustments-title">
      <div className="caddie-adjustments-heading">
        <h3 id="caddie-adjustments-title">Caddie Adjustments</h3>
        <small>Fine-tune how your caddie talks through each shot.</small>
      </div>

      <div className="caddie-voice-row">
        <div className="profile-field caddie-voice-field">
          <label htmlFor="profile-caddie-voice">Caddie Voice</label>
          <SelectButton
            id="profile-caddie-voice"
            value={voicePreference}
            onChange={(event) => event.value && onVoicePreferenceChange(event.value)}
            options={CADDIE_VOICE_OPTIONS}
            allowEmpty={false}
            className="caddie-voice-toggle"
          />
          <small className="profile-hint">Female uses Amanda. Male uses Andrew.</small>
        </div>

        {renderRadioGroup('talkativeness', 'How talkative', talkativeness, TALKATIVENESS_OPTIONS, onTalkativenessChange)}
      </div>

      {renderRadioGroup('voice-delivery', 'Voice style', voiceStyle, VOICE_DELIVERY_OPTIONS, onVoiceStyleChange)}
      {renderRadioGroup('detail-level', 'Recommendation depth', recommendationDepth, DETAIL_OPTIONS, onRecommendationDepthChange)}
      {renderRadioGroup('focus-area', 'Primary focus', primaryFocus, FOCUS_OPTIONS, onPrimaryFocusChange)}
    </section>
  );
};

export default CaddieAdjustments;